import React, { useState, useMemo, useRef, useEffect } from "react";
import ReactDOM from 'react-dom';
import { camelCase, startCase } from "lodash";

import { BasicAction }   from "widgets/ActionMenu";
import {
	SOCIAL_AD_TYPE_LIST,
	SOCIAL_AD_TYPES,
	ADSTUDIO_TYPE,
	INCOMPLETE_STATUS,
	VAST_AUDIO, NATIVE_IMAGE,
	MAX_DYNAMIC_SOCIAL_VERSIONS,
	DYNAMIC_SOCIAL_AD_TYPES,
	IMPOSIUM_TYPE,
	DYNAMIC_PREROLL,
	CTV_CREATIVE,
	SUPPRESS_ALL_GROWL,
	JS_VPAID,
	META_PAC_PARTNERS
} from "./constants";
import { META_PAC_EXTERNAL_IDS, PAC_FORM_TYPES } from "widgets/social2/constants/common";

import { isDVAST, isTranscoding, isTranscodingError } from "./CreativeActions";
import { PreviewCreative } from "./Preview";

export const SHOW_TRACKING = true;
export function noop(){}

export function getEnvironmentName(enVal){
	if (enVal.toLowerCase() === 'dev') {
		return 'dev';
	} else if (enVal.toLowerCase() === 'qa') {
		return 'QA';
	} // explicitly list environments we want to display, intentionally exclude production
	return null;
}

export function hasPermission(user, domain, action){
	/*
		user data has permissions structured like:

		{
			permissions: {
				[Creative View]: ["Upload Assets", "Rename", "Delete", ...],
				<domain>: [<action>, <action>, ...],
				...
			}
		}
	*/
	const { permissions = {} } = user || {};
	const permittedActions = permissions[domain] || [];
	return permittedActions.includes(action);
}

export const SECONDS_PER_MIN = 60;

// Base 10 byte multiples
export const KILO = 1000;
export const MEGA = KILO * KILO;
export const GIGA = MEGA * KILO;

// Binary byte multiples
export const KIBI = 1024;
export const MEBI = KIBI * KIBI;
export const GIBI = MEBI * KIBI;

const DECIMAL_SIZE_UNITS = [
	"Bytes",
	"KB",
	"MB",
	"GB",
	"TB",
];

const BINARY_SIZE_UNITS = [
	"Bytes",
	"KiB",
	"MiB",
	"GiB",
	"TiB",
];

const DIM_PLACES = 2;

/**
 * Converts bytes to multiples
 */
export function formatBytes(value, places = DIM_PLACES, useBinary = false){
	const base = useBinary ? KIBI : KILO;
	const units = useBinary ? BINARY_SIZE_UNITS : DECIMAL_SIZE_UNITS;
	const bytes = parseFloat(value);
	let logPower = 0;
	let kibs = 0;
	let size = null;

	if(bytes === 0) { return "0 Bytes"; }
	if(isNaN(bytes)) { return "--"; }

	logPower = Math.floor(Math.log(bytes) / Math.log(base));
	kibs = parseFloat((bytes / Math.pow(base, logPower)).toFixed(places));
	size = units[logPower];

	return `${kibs} ${size}`;
}
/**
 * [isSocialCreative description]
 * 		- Used to determine actions in the Creative Grid
 *
 * @return {Boolean} Is the given AdType of a social type?
 */
export function isSocialCreative(dependentValues){
	let { adType } = dependentValues;

	return SOCIAL_AD_TYPE_LIST.includes(adType);
}

/**
 * Determines if we are at the limit of dynamic social versions allowed within a creative
 * @param {*} length the amount of versions within a creative
 * @param {*} dependentValues the creative details' values
 */

export function isMaxDynamicSocialVersions(length, dependentValues = {}) {
	const { adType, isDynamic } = dependentValues;

	return length >= MAX_DYNAMIC_SOCIAL_VERSIONS && DYNAMIC_SOCIAL_AD_TYPES.includes(adType) && isDynamic;
}

export function isFacebookCreative(dependentValues = {}) {
	let { adType } = dependentValues;
	return !!(adType && (adType === SOCIAL_AD_TYPES.FACEBOOK_IMAGE || adType === SOCIAL_AD_TYPES.FACEBOOK_VIDEO));
}

export function isSocialCarouselCreative(dependentValues = {}) {
	const { adType } = dependentValues;
	return isSocialCreative(dependentValues) && /carousel/gi.test(adType);
}
// isNativeCreative...
export function isNativeImageCreative(dependentValues = {}) {
	const { adType } = dependentValues;
	return adType === NATIVE_IMAGE;
}

/**
 * isAdstudioCreative
 * @param { { origin: string } } Creative origin
 */
export function isAdstudioCreative(dependentValues) {
	const { origin } = dependentValues;

	return origin === ADSTUDIO_TYPE;
}

/**
 * Checks if creative the is an imposium creative
 * @param {Object} dependentValues
 * @param {string} dependentValues.origin
 */
export function isImposiumCreative(dependentValues = {}) {
	const { origin } = dependentValues;

	return origin === IMPOSIUM_TYPE;
}
export function isVideoBuilderCreative(dependentValues = {}){
	const { origin, html5Meta, adType } = dependentValues;
	return !origin && (html5Meta?.apiVersion === "2.8") && (adType === DYNAMIC_PREROLL);
}

export function isDisplayHTMLTagCreative(dependentValues = {}){
	const { adType } = dependentValues;
	return adType === "display_html_tag";
}

export function isCtvCreative(dependentValues = {}) {
	const { adType } = dependentValues;
	return adType === CTV_CREATIVE;
}

export function isVASTVideoCreative(dependentValues = {}){
	let { adType } = dependentValues;
	if(!adType){ return false;}

	return (adType === "preroll");
}

export function isVASTAudioCreative(dependentValues = {}){
	let { adType } = dependentValues;
	if(!adType){ return false;}

	return (adType === VAST_AUDIO);
}

export function isVASTCreative(dependentValues = {}) {
	return (isVASTVideoCreative(dependentValues) || isVASTAudioCreative(dependentValues));
}

export function isVASTTranscoding(dependentValues){
	if(!isVASTCreative(dependentValues)){
		return false;
	}
	return (isTranscoding(dependentValues.approval));
}

export function isVASTTranscoded(dependentValues){
	if(!isVASTCreative(dependentValues)){
		return false;
	}
	return !(isTranscoding(dependentValues.approval)||isTranscodingError(dependentValues.approval));
}

export function isVASTTranscodingError(dependentValues){
	if(!isVASTCreative(dependentValues)){
		return false;
	}
	return (isTranscodingError(dependentValues.approval));
}

export function isDVASTTranscoding(dependentValues){
	if(!isDVAST(dependentValues.adType)){
		return false;
	}
	return (isTranscoding(dependentValues.approval));
}

export function isDVASTTranscodingError(dependentValues){
	if(!isDVAST(dependentValues.adType)){
		return false;
	}
	return (isTranscodingError(dependentValues.approval));
}

export function isIncomplete(dependentValues){
	let { approval } = dependentValues;
	return INCOMPLETE_STATUS.includes(approval);
}

export function isSocialPreviewDisabled(dependentValues = {}) {
	const { adType } = dependentValues;
	return [SOCIAL_AD_TYPES.TWITTER_CAROUSEL].includes(adType);
}

export function isSocialEditDisabled(dependentValues = {}) {
	const { adType } = dependentValues;
	return [SOCIAL_AD_TYPES.TWITTER_CAROUSEL].includes(adType);
}

export function isImposiumValid(dependentValues = {}) {
	const { organizationId, storyId } = dependentValues;

	return [
		!!organizationId,
		!!storyId,
		organizationId !== -1,
		storyId !== -1,
		isImposiumCreative(dependentValues)
	].every(cond => cond);
}

export const transcodingChecks = {
	transcoding: dependentValues => isVASTTranscoding(dependentValues) || isDVASTTranscoding(dependentValues),
	transcoded: isVASTTranscoded,
	error: dependentValues => isVASTTranscodingError(dependentValues) || isDVASTTranscodingError(dependentValues),
};
/*
	The API's advancedFilter syntax uses parens to group arguments, etc., but
	sometimes we want to filter on a column value that includes parens, but
	the API doesn't delimit parameters with quotation marks.

	The API supports this by escaping parens in values by surrounding them
	with underscores.

	e.g. If we want to search for something having the `name` "Campaign", or
	"(Other)Campaign", this should look like:
		?advancedFilter=in(name,Campaign,_(_Other_)_Campaign)
*/
function underscoreEscape(match){
	return `_${match}_`;
}

const parenPattern = new RegExp(/\(|\)/g); // "(" or ")", compiled once for most efficient reuse.

export function escapeParensForAPI(param){
	// Convert param to string, so replace will always be defined
	return `${param}`.replace(parenPattern, underscoreEscape);
}

/*
	The API needs to see an escape character before any special characters such as underscore "_"
	or data will not be seen/filtered correctly.
	This function could be expanded upon to escape more characters before sending it to the api if needed.
*/
export function escapeUnderscoreForAPI(str = "") {
	return str.replace(/_/gi, "\\_");
}

export function capFirst(str = ""){
	// Just capitalizes the first letter in a string.  Sometimes we can't avoid case sensitive checks, etc.
	return str[0]?.toUpperCase() + str.slice(1);
}


/**
 * [tooltip description]
 * @param  {string} message The String to use
 * @return {object}         Object to send to rhombus tooltip
 */
export function tooltip(message, direction = "right"){
	return {
		description: message,
		direction,
	};
}

export function TextOverflow({ value }){
	return <span title={value} className="name-overflow">{value}</span>;
}

export function PLACEHOLDER_ACTION(name, iconName) {
	return {
		component: () => {
			return (
				<BasicAction
					iconName={ iconName }
					name={ name }
					handleClick={ () => {
						console.log(`"${name}" action isn't implemented.`);
					} }
				/>
			);
		},
	};
}

export function isDynamic({ dynamicTarget, isDynamic }){
	return dynamicTarget && !/non-dynamic/gi.test(dynamicTarget) || isDynamic;
}

export function isConceptDynamic(concept){
	// A Concept is dynamic if any of its child Creatives are dynamic.
	let { creatives = []} = concept;
	return creatives.some(creative => isDynamic(creative));
}

export function isDisplayCreative(dependentValues) {
	return /HTML_onpage|HTML_standard|HTML_expand|altimage|display_html_tag/gi.test(dependentValues?.adType);
}

export function isOpenWebDisplayCreative(dependentValues) {
	return /HTML_onpage|HTML_standard/i.test(dependentValues?.adType);
}

export function isAfterEffectsCreative(dependentValues) {
	return /after_effects/gi.test(dependentValues?.origin);
}

export function isDynamicRenderSocial(dependentValues) {
	if (!dependentValues || !isSocialCreative(dependentValues) || !isDynamic(dependentValues)) {
		return false;
	}
	return isImposiumCreative(dependentValues) || isAfterEffectsCreative(dependentValues) || isAdstudioCreative(dependentValues);
}

export function pluralize(term, count, ext = 's') {
	return `${term}${(count > 1) ? ext : ''}`;
}

export function useModalStack(commonProps = {}){
	const [modalStack, setModalStack] = useState([]);

	/*
		pushModal and popModal were rewritten to use functions for their calls to setModalStack.

		For some reason, if they're not written this way, they get an incorrect value for
		modalStack, which made it impossible to grow the stack beyond the first item,
		or not pop the entire stack.

		i.e. prevStack and modalStack have different values inside those functions.

		I have no idea why this happens.
	*/
	function pushModal(modal){
		setModalStack((prevStack) => {
			let newStack = [...prevStack, modal];
			return newStack;
		});
	}

	function popModal(){
		setModalStack((prevStack) => {
			return prevStack.slice(0, -1);
		});
	}

	function clearStack(){
		setModalStack([]);
	}

	const head = useMemo(() => {
		let stackLength = modalStack.length;
		if(stackLength){
			const CurrentModal = modalStack[stackLength - 1];
			function Configured(props){
				let navigationActions = {
					...(commonProps.navigationActions || []),
					...(props.navigationActions || []),
					pushModal,
					popModal,
					clearStack,
				};

				let modalProps = {
					...commonProps,
					...props,
					navigationActions,
				};
				return <CurrentModal {...modalProps} />;
			}
			return Configured;
		}
		return null;
	}, [modalStack]);

	return [modalStack, { pushModal, popModal, clearStack }, head];
}

function matchById(itemA, itemB){ return itemA.id === itemB.id; }

export function mergeItems(items, newItems, matcher) {
	return newItems.reduce((prev, item) => {
		return mergeItem(prev, item);
	}, items);
}

/**
	Converts an object to a base64 string.
	This is useful for passing objects in data URLs, etc.
*/
export function objectToBase64EncodedString(obj){
	const jsonVariableConfig = JSON.stringify(obj);
	const encoder = new TextEncoder();
	// Next 5 lines are necessary to handle non Latin1 characters
	// otherwise we could use btoa directly
	const encoded = encoder.encode(jsonVariableConfig);
	let binaryString = '';
	for (let i = 0; i < encoded.length; i++) {
		binaryString += String.fromCharCode(encoded[i]);
	}
	return btoa(binaryString);
}

export function mergeItem(items, newItem, matcher){
	/*
		Sometimes we have records that we want to inject into grids that we get from sockets, or after
		uploads, etc.

		The records aren't always new, so we want to be able to replace a row with new data if the row is
		already in the grid, or to add it on top if it isn't.

		This function just returns a new array with `newItem` merged into it, which is suitable for use in
		reducers.

		By default, we just match items by comparing their `id` property, but you can specify a matching
		function as a third argument if you need a different strategy.
	*/
	if(!newItem){
		return items;
	}

	matcher = matcher || matchById;

	let itemIndex = items.findIndex((item) => { return matcher(item, newItem); });
	if(itemIndex === -1){
		// There's no matching item, so just add the new item to the front.
		return [newItem, ...items];
	}

	return [
		...items.slice(0, itemIndex),
		newItem,
		...items.slice(itemIndex + 1)
	];
}


export function labelWithCount(label, count = 1){
	/*
		When we have buttons that show counts for potentially bulk actions, we don't want to show the count
		at all, if there's only one item.

		This simplifies reuse between bulk and single actions.
	*/
	if(count > 1){
		return `${label} (${count})`;
	}
	return label;
}

export function getFilenameExtension(filename = ""){
	return filename.split('.').pop();
}

export function splitFilename(filename){
	let base = filename;
	let extension;

	let lastDotIndex = filename.lastIndexOf(".");
	if (lastDotIndex >= 0) {
		base = filename.substring(0, lastDotIndex);
		extension = filename.substring(lastDotIndex);
	}
	return [base, extension];
}

export function invalidCharacters(pattern, string) {
	const invalid = new Set();
	for(let i = 0; i < string?.length; i++) {
		if(pattern.test(string[i]) === false){
			invalid.add(string[i]);
		}
	}
	return [...invalid];
}

/**
 * Decodes URL-encoded characters (e.g. `Exclaim%21.mp4` -> `Exclaim!.mp4`) for display.
 * `decodeURIComponent` throws on malformed sequences (e.g. a bare `%`), so we fall back to the raw value.
 * @param   {string}  value   The string to decode.
 * @returns {string}  The decoded string, or `value` unchanged if it is not a string or cannot be decoded.
 */
export function safeDecodeURIComponent(value){
	if(typeof value !== "string"){ return value; }

	try {
		return decodeURIComponent(value);
	} catch(err){
		return value;
	}
}

export function getFilenames(fileList){
	/*
		WHen you get the values from a file input, it's a FileList object,
		which can't be iterated over normally.

		https://developer.mozilla.org/en-US/docs/Web/API/FileList

		You have to use `FileList.item(index)` to access items in the list.

		This function just extracts the filenames from a FileList and returns them as an array.
	*/
	let names = [];
	for(let i = 0; i < fileList?.length; i++){
		names.push(fileList.item(i).name);
	}

	return names;
}

export function buildFormData(fileList){
	/*
		Our upload APIs tend to want FormData with a particular structure:
		[
			"files": File1,
			"files": File2,
			...
		]

		This function just returns FormData in this format with a given FileList,
		i.e. the input.files value when using a file input.

		The returned object can be further modified with more appended values, etc., if needed.
	*/
	let formData = new FormData();
	for(let i = 0; i < fileList.length; i++){
		formData.append("files", fileList.item(i));
	}

	return formData;
}

export function creativePreviewURL(creative, forceNew) {
	const { id, adType } = creative || {};
	if(!id || !adType){ // Assuming 0 isn't a valid id
		console.error("Attempted to get the preview URL for an invalid Creative");
		return;
	}
	return PreviewCreative.private(creative, forceNew).url;
}

export function getYoutubeEmbedUrl(youtubeUrl) {
	let videoId = youtubeUrl?.match(/[?&]v=([^&]+)/)?.[1];
	if(!videoId){ return null; }
	return `https://www.youtube.com/embed/${videoId}`;
}

export function toFriendlyErrorMessage(error){
	let match = /message=(.*?)(?:,\s*\w+=|}\s*$)/.exec(error);
	if(match){ return match[1].trim(); }

	return error;
}

export function ConditionalWrapper({ condition, wrapper, children }) {
	return condition ? wrapper(children) : children;
}

export function titleCase(value) {
	return startCase(camelCase(value));
}

export function buildAssetGenerationFailMessage(resp, success){
	if(success){ return; }

	return {
		type:"error",
		id:new Date().getTime(),
		message:"Try to modify prompt and retry.",
		messageHeader:"Problem Generating Assets",
	};
}

export function buildAddVersionsToLibraryMessage(resp, success) {
	if (success) {
		return {
			type: "success",
			id: new Date().getTime(),
			message: resp.data?.request?.description ?? "Versions successfully added to library",
			messageHeader: "Vesions added to library",
		};
	} else {
		return {
			type: "error",
			id: new Date().getTime(),
			message: resp.data?.description ?? "There was a problem adding versions to the library.",
			messageHeader: resp.data.name,
		};
	}
}

export function buildNoFileMessage(resp, success){
	if(success){ return; }

	return {
		type:"error",
		id:new Date().getTime(),
		message:"There was a problem downloading, we were unable to find the file.",
		messageHeader:"Problem Downloading",
	};
}

export function getUploadFiles(fileList) { /*
		Takes a FileList and converts it into an array of objects to upload.
	*/
	const items = [];

	for (let i = 0; i < fileList.length; i++) {
		const file = fileList.item(i);

		items.push({
			name: file.name,
			size: file.size,
			type: file.type,
			lastModifiedDate: file.lastModifiedDate,
			progress: 0,
		});
	}

	return items;
}

function *generateID(){
	let id = 0;
	while(true){
		id = id + 1;
		yield id;
	}
}

export const clientIDGenerator = generateID();

export function getNewClientID(){
	return clientIDGenerator.next().value;
}

// TODO: this was copied from PreviewBlade.js
// 		 we should update that file to import this function rather than having a duplicate
export function useClickOutsideHandler(onClickOutside = noop){
	let compRef = useRef();

	function handler(e){
		let node = ReactDOM.findDOMNode(compRef.current);
		if(node){
			if(!node.contains(e.target)){
				onClickOutside();
			}
		}
	}

	useEffect(() => {
		document.addEventListener("mousedown", handler, true);

		return () => {
			document.removeEventListener("mousedown", handler, true);
		};
	}, []);

	return [compRef];
}

export async function copyToClipboard(value){
	if(navigator.clipboard){
		return navigator.clipboard.writeText(value);
	}

	/*
		If the clipboard API isn't available, we fallback to adding a node to the DOM.
		Using a `pre` will preserve whitespace, etc., and avoids issues with focus-traps,
		e.g. from our modals, stealing focus and interfering with selection.
	*/
	let pre = document.createElement("pre");
	pre.textContent = value;

	pre.style = {
		webkitUserSelect: "auto",
		userSelect: "all",
	};

	document.body.appendChild(pre);

	const selection = window.getSelection();
	const range = window.document.createRange();
	selection.removeAllRanges();
	range.selectNode(pre);
	selection.addRange(range);

	let success = false;
	try{
		success = window.document.execCommand("copy");
	} finally {
		selection.removeAllRanges();
		window.document.body.removeChild(pre);
	}
	if(success === false){
		throw new Error("Copying text failed. document.execCommand('copy') may no longer be supported.");
	}
}

export const findPlacementByAspect = (placements=[], aspect) => {
	if (!placements?.length || !aspect) {
		return null;
	}

	return placements.find((placement) => {
		return placement?.type?.name?.toLowerCase() === aspect && placement?.status === "active";
	});
};

const findPlacementByExternalId = (placements=[], externalId) => {
	if (!placements?.length || !externalId) {
		return null;
	}
	return placements.find((placement) => {
		return placement?.status === "active" && placement?.type?.externalId === externalId;
	}) || null;
};

const findReelsPlacement = (placements=[], partner) => {
	if (!placements?.length) {
		return null;
	}
	const legacyReelsPlacement = findPlacementByExternalId(placements, META_PAC_EXTERNAL_IDS[PAC_FORM_TYPES.REELS]);
	const facebookReelsPlacement = findPlacementByExternalId(placements, META_PAC_EXTERNAL_IDS[PAC_FORM_TYPES.FB_REELS]);
	const instagramReelsPlacement = findPlacementByExternalId(placements, META_PAC_EXTERNAL_IDS[PAC_FORM_TYPES.IG_REELS]);
	if (partner === META_PAC_PARTNERS.INSTAGRAM) {
		return instagramReelsPlacement || legacyReelsPlacement;
	}
	return facebookReelsPlacement || legacyReelsPlacement;
};

//for getting pac placement by fbMO v2 placement type. (e.g. maps 'story' to 'vertical', etc)
export const getPlacementByType = (placements=[], pacType, partner) => {
	let square = findPlacementByAspect(placements, "square");
	let vertical = findPlacementByAspect(placements, "vertical");
	let reels = findPlacementByAspect(placements, "reels") || findReelsPlacement(placements, partner);

	if (pacType === "feeds") {
		return square || null;
	} else if  (pacType === "stories") {
		return vertical || null;
	} else if  (pacType === "reels") {
		return reels || vertical || null;
	}
	return null;
};

export const iframeDimsRegex = new RegExp(/("width":\s\d+|"height":\s\d+)/gm);

export function getManifest(libraryId, creativeId){
	return (dispatch) => {
		return dispatch({
			type:"LOAD_MANIFEST_AJAX",
			AJAX:{
				url:`asset-manager/${libraryId}/${creativeId}/adstudio-render-${creativeId}/manifest.js`,
				params:{ download: false },
				success:"LOAD_MANIFEST_SUCCESS",
				error:"LOAD_MANIFEST_ERROR",
			},
			meta: SUPPRESS_ALL_GROWL,
		});
	};
}

export function promiseChain(promiseArray){
	/*
		This basically works like a Promise.allSettled, but the requests are made
		in series, rather than in parallel.
	*/
	const responses = [];
	const chain = promiseArray.reduce((prev, next) => {
		return prev.then((response) => {
			responses.push(response);
			return next(response);
		});
	}, Promise.resolve());

	return chain.then((response) => {
		responses.shift(); // The first item is from the initial Promise.resolve
		responses.push(response);
		return responses;
	});
}

export function hasContentFeedAttached(dependentValues) {
	return !!dependentValues?.contentFeedUrl;
}

export function isEnabledForContentFeed(dependentValues) {
	const { adType } = dependentValues;
	// Content feed is enabled for dynamic display and dynamic video
	// JS_VPAID is a form of dynamic video
	return isDynamic(dependentValues) && (
		isDisplayCreative(dependentValues)
		|| adType === DYNAMIC_PREROLL
		|| adType === JS_VPAID
	);
}
